import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Box, Button, CircularProgress, Grid, List, Skeleton, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataGrid, getGridDateOperators } from '@mui/x-data-grid';
import { DataGridPro, getGridDateOperators as getGridDateOperatorsPro } from '@mui/x-data-grid-pro';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory } from 'react-router';
import isEqual from 'lodash/isEqual';

import { DataAPIContext, GET_LIST, GET_ONE } from '../api';
import SelectAllBanner from './SelectAllBanner';
import { serverDateOperators, toApiFilter } from './gridDateFilters';
import { useIsWidthUp } from '../utils';

import AutoRefreshGridFooter, { useAutoRefresh } from './AutoRefreshGridFooter';

const dedupeRowsById = (rows) => {
    if (!Array.isArray(rows) || rows.length < 2) {
        return rows;
    }
    const seen = new Set();
    return rows.filter((row) => {
        const id = row?.id;
        if (id === undefined || id === null) {
            return true;
        }
        if (seen.has(id)) {
            console.warn('RecordList: duplicate row id omitted', id);
            return false;
        }
        seen.add(id);
        return true;
    });
};

const GridSkeleton = (props) => {
    const animation = props.animation || 'wave';
    return (
        <div>
            <Typography variant="h4">
                <Skeleton variant="text" animation={animation} />
            </Typography>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={3}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
            </Grid>
        </div>
    );
};
const useStyles = makeStyles((theme) => ({
    sortButton: {
        minWidth: theme.spacing(4),
        minHeight: theme.spacing(4),
        padding: theme.spacing(0.5),
        borderRadius: theme.spacing(1),
    },
    itemListRoot: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(12),
        overflowY: 'auto',
        flexGrow: 1,
        minHeight: 0,
        WebkitOverflowScrolling: 'touch',
    },
    itemListRootNoMarginBottom: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(4),
        overflowY: 'auto',
        flexGrow: 1,
        minHeight: 0,
        WebkitOverflowScrolling: 'touch',
    },
    itemListRootNatural: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        overflowY: 'visible',
        flexGrow: 0,
    },
    withBorder: {
        border: '1px solid #d7d7d7',
        borderRadius: '15px',
    },
    mobileWrapper: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: 0,
    },
    mobileWrapperNatural: {
        height: 'auto',
        minHeight: 0,
    },
    paginationButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: '1.5rem',
    },
}));

/**
 * Toolbar wrapper that renders the original toolbar plus the SelectAllBanner.
 * Defined outside RecordList so the component identity is stable across renders;
 * banner state is passed through componentsProps.toolbar.
 */
const ToolbarWithSelectAll = ({ OrigToolbar, selectAllBannerProps, ...toolbarProps }) => (
    <>
        {OrigToolbar && <OrigToolbar {...toolbarProps} />}
        {selectAllBannerProps && <SelectAllBanner {...selectAllBannerProps} />}
    </>
);

const RecordList = (props) => {
    const history = useHistory();
    const searchParams = new URLSearchParams(history.location.search);

    const dataAPI = React.useContext(DataAPIContext);
    const resource = props.resource;
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const isSmallScreen = !useIsWidthUp('sm');

    const disableSelectionOnClick = props.disableSelectionOnClick === void 0 ? true : props.disableSelectionOnClick;
    const [total, setTotal] = React.useState(null);
    const [rows, setRows] = React.useState([]);
    const [page, setPage] = React.useState(props.page || searchParams.get('page') || 1);
    const [perPage, setPerPage] = React.useState(props.perPage || searchParams.get('pageSize') || 25);
    const defaultSortModel = props.sortModel || [{ field: 'id', sort: 'asc' }];
    const [sortModel, setSortModel] = React.useState(defaultSortModel);
    // Start in the loading state (unless rows are provided statically) so the
    // first paint shows the spinner rather than the empty-state message.
    const [loading, setLoading] = React.useState(!props.staticOptions);
    const [filterList, setFilterList] = React.useState([]);
    const [selectionModel, setSelectionModel] = React.useState([]);
    const [asyncCountHandle, setAsyncCountHandle] = React.useState(null);
    const rowsPerPageOptions = props.rowsPerPageOptions || [10, 25, 50, 100];
    const totPages = Math.floor(total / perPage) + (total % perPage ? 1 : 0);
    const [prevQuery, setPrevQuery] = React.useState(null);
    const [prevApiFilters, setPrevApiFilters] = React.useState(null);
    const [prevPageReset, setPrevPageReset] = React.useState(false);

    // CP-8184: consumers rebuild the columns array on every render, and whenever the
    // columns prop changes identity the v5 grid re-derives visibility from the fresh
    // column defs — dropping anything hidden via the columns panel (any parent
    // re-render, e.g. a row select or menu toggle, was enough). Keeping the model
    // controlled here makes hiding survive re-renders; localStorage makes it survive
    // reloads (same pattern as TripUserManager/ActivityList/AgentMetricsList).
    // Consumers that pass their own columnVisibilityModel stay fully in charge.
    const hasOwnColumnVisibilityModel = props.columnVisibilityModel !== undefined;
    const columnVisibilityStorageKey = `RecordList.columnVisibilityModel.${resource}`;
    const [internalColumnVisibilityModel, setInternalColumnVisibilityModel] = React.useState(() => {
        // Columns hidden by default (v5 `hide: true`) — the controlled model takes
        // precedence over the flag, so it has to carry them.
        const defaults = {};
        (props.columns || []).forEach((c) => {
            if (c.hide) {
                defaults[c.field] = false;
            }
        });
        try {
            const saved = window.localStorage.getItem(columnVisibilityStorageKey);
            if (saved) {
                return { ...defaults, ...JSON.parse(saved) };
            }
        } catch {
            /* quota / privacy mode / malformed */
        }
        return defaults;
    });
    const onColumnVisibilityModelChange = React.useCallback((model, details) => {
        if (!hasOwnColumnVisibilityModel) {
            setInternalColumnVisibilityModel(model);
            try {
                window.localStorage.setItem(columnVisibilityStorageKey, JSON.stringify(model));
            } catch {
                /* quota / privacy mode */
            }
        }
        props.onColumnVisibilityModelChange?.(model, details);
    }, [hasOwnColumnVisibilityModel, columnVisibilityStorageKey, props.onColumnVisibilityModelChange]);

    const orderingField = props.orderingField || 'sequence';
    const isReorderingAllowed = filterList?.length === 0 && sortModel?.length === 1 && sortModel[0]?.field === orderingField;
    const isPro = props.flavor === 'pro';

    // Drag-resized widths (pro flavor, opt-in via persistColumnWidths). The grid keeps a
    // dragged width in its own column state, but every render here hands it a fresh
    // columns array, and DataGridPro v6 re-applies each definition's width/flex when that
    // happens: the column snapped back on the next fetch. So the widths live here (and in
    // localStorage) and are baked into the definitions, like CampaignListGrid does.
    const persistColumnWidths = isPro && !!props.persistColumnWidths;
    const columnWidthsStorageKey = `RecordList.columnWidths.${resource}`;
    const [columnWidths, setColumnWidths] = React.useState(() => {
        if (!persistColumnWidths) {
            return {};
        }
        try {
            const raw = JSON.parse(window.localStorage.getItem(columnWidthsStorageKey) || '{}');
            const clean = {};
            for (const [field, width] of Object.entries(raw)) {
                if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
                    clean[field] = width;
                }
            }
            return clean;
        } catch {
            /* quota / privacy mode / malformed */
            return {};
        }
    });
    const { onColumnWidthChange: onColumnWidthChangeProp } = props;
    const onColumnWidthChange = React.useCallback((params, event, details) => {
        if (persistColumnWidths && Number.isFinite(params?.width) && params?.colDef?.field) {
            setColumnWidths((prev) => {
                const next = { ...prev, [params.colDef.field]: params.width };
                try {
                    window.localStorage.setItem(columnWidthsStorageKey, JSON.stringify(next));
                } catch {
                    /* quota / privacy mode — the width still applies for this session */
                }
                return next;
            });
        }
        onColumnWidthChangeProp?.(params, event, details);
    }, [persistColumnWidths, columnWidthsStorageKey, onColumnWidthChangeProp]);

    // Drag & drop row reordering (DataGridPro only). The grid disables dragging whenever a
    // sortModel is active, so while the data is in plain sequence order we pass an empty
    // sortModel to the grid and keep sorting by the ordering field server-side.
    const dragRowReordering = !!props.rowReordering && isPro;
    const sequenceOrderedView =
        filterList?.length === 0 &&
        (!sortModel?.length || (sortModel.length === 1 && sortModel[0]?.field === orderingField && sortModel[0]?.sort !== 'desc'));
    const dragReorderActive = dragRowReordering && sequenceOrderedView;

    // --- Select-all-matching support ---
    // Strategy: store the full select-all IDs in a ref, only push
    // current-page IDs into the grid's selectionModel. This avoids
    // fighting the DataGrid's internal reconciliation which strips
    // IDs not present in the current rows.
    const enableSelectAll = props.checkboxSelection !== false && props.enableSelectAll !== false;
    const [selectAllMeta, setSelectAllMeta] = React.useState(null);
    // selectAllMeta: { totalCount, limit, ids }  (ids = full list from API)
    const [selectAllLoading, setSelectAllLoading] = React.useState(false);
    const selectAllInProgressRef = React.useRef(false);

    // Stable ref for parent callback to avoid recreating doSelectAll/doDeselectAll on every render
    const onSelectionChangeRef = React.useRef(props.onSelectionModelChange);
    onSelectionChangeRef.current = props.onSelectionModelChange;

    const doSelectAll = React.useCallback(async () => {
        setSelectAllLoading(true);
        try {
            const filtersJson = JSON.stringify([
                ...(filterList || []).map((f) => toApiFilter(f, props.columns)),
                ...(props.apiFilters || []),
            ]);
            let url = `select_all_ids/?filters=${encodeURIComponent(filtersJson)}`;
            if (props.query) url += `&query=${encodeURIComponent(props.query)}`;
            if (props.selectAllExtraParams) {
                Object.entries(props.selectAllExtraParams).forEach(([k, v]) => {
                    if (v != null) url += `&${k}=${encodeURIComponent(v)}`;
                });
            }
            const res = await dataAPI(GET_ONE, resource, { urlSuffix: url });
            if (res && res.statusCode === 200 && res.data) {
                const allIds = res.data.ids;
                // Only push current-page IDs into the grid — store full list in meta
                const pageIdSet = new Set(rows.map(r => r.id));
                const pageIds = allIds.filter(id => pageIdSet.has(id));
                // Flag so onSelectionModelChange knows not to clear the meta
                selectAllInProgressRef.current = true;
                setSelectAllMeta({ totalCount: res.data.total_count, limit: res.data.limit, ids: allIds });
                setSelectionModel(pageIds);
                onSelectionChangeRef.current && onSelectionChangeRef.current(allIds, allIds);
                if (res.data.total_count > res.data.limit) {
                    enqueueSnackbar(
                        `Selected ${res.data.limit} of ${res.data.total_count} matching records (maximum)`,
                        { variant: 'warning', autoHideDuration: 6000 });
                }
            } else {
                enqueueSnackbar('Error selecting all records', { variant: 'error' });
            }
        } catch (e) {
            console.error('RecordList.doSelectAll error:', e);
            enqueueSnackbar('Error selecting all records', { variant: 'error' });
        } finally {
            setSelectAllLoading(false);
        }
    }, [dataAPI, resource, rows, filterList, props.columns, props.apiFilters, props.query, props.selectAllExtraParams, enqueueSnackbar]);

    const doDeselectAll = React.useCallback(() => {
        setSelectionModel([]);
        setSelectAllMeta(null);
        onSelectionChangeRef.current && onSelectionChangeRef.current([], []);
    }, []);

    // Clear selectAllMeta when filters/query change
    React.useEffect(() => {
        setSelectAllMeta(null);
    }, [props.query, props.apiFilters]);
    // --- End select-all ---

    // Mirrors `loading` for the auto-refresh poll, as a ref because the poll runs in
    // a setInterval closure; see useRefreshTrigger for why ticks wait on it.
    const fetchInFlightRef = React.useRef(false);
    const { refreshTrigger, refreshTriggerValue } = useAutoRefresh(fetchInFlightRef);

    //Select the previous page and pageSize saved in browser history location search params
    React.useEffect(() => {
        let previous_page_size = searchParams.get('pageSize')
        let previous_page = searchParams.get('page')

        if (!props.disablePageUrlData) {
            if (previous_page_size) {
                setPerPage(parseInt(previous_page_size));
            }
            if (previous_page) {
                setPage(parseInt(previous_page));
            }
            return;
        }

    }, []);


    // allow controlling the selected rows as props (removing the selection after updating etc..)
    React.useEffect(() => {
        setSelectionModel(props.selectionModel ? props.selectionModel : []);
    }, [props.selectionModel]);

    React.useEffect(() => {
        if (!isEqual(props.query, prevQuery) || !isEqual(props.apiFilters, prevApiFilters) || props.pageReset !== prevPageReset) {
            setPage(props.page || 1);
            setPrevQuery(props.query);
            setPrevApiFilters(props.apiFilters);
            setPrevPageReset(props.pageReset);
        }
    }, [props.query, props.apiFilters, props.pageReset]);

    React.useEffect(() => {
        if (props.staticOptions) {
            setTotal(props.staticOptions.length);
        }
    }, [props.staticOptions]);

    React.useEffect(() => {
        props.setIsLoading?.(loading)

    }, [loading]);

    const components = {
        ...props.slots,
        NoRowsOverlay: () => (
            <Box display="flex" height="100%" alignItems="center" justifyContent="center" flexDirection="column" gap="1rem">
                <div>
                    <svg width="86" height="87" viewBox="0 0 86 87" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_57_14421)">
                            <path d="M43 86.5C66.7482 86.5 86 67.2482 86 43.5C86 19.7518 66.7482 0.5 43 0.5C19.2518 0.5 0 19.7518 0 43.5C0 67.2482 19.2518 86.5 43 86.5Z" fill="#EEF5FA" />
                            <path d="M77.2072 8.36279H14.2266V82.072H77.2072V8.36279Z" fill="#AEDCFF" />
                            <path d="M74.4904 6.64613H11.5098V80.3553H74.4904V6.64613Z" fill="white" />
                            <line x1="30.7071" y1="32" x2="56" y2="57.2929" stroke="#007AFF" strokeLinecap="round" />
                            <line x1="0.5" y1="-0.5" x2="36.2696" y2="-0.5" transform="matrix(-0.707107 0.707107 0.707107 0.707107 56 32)" stroke="#007AFF" strokeLinecap="round" />
                        </g>
                        <defs>
                            <clipPath id="clip0_57_14421">
                                <rect width="86" height="86" fill="white" transform="translate(0 0.5)" />
                            </clipPath>
                        </defs>
                    </svg>
                </div>
                <Typography fontWeight={400} fontSize={16}>
                    {props.noMatchesLabel ? props.noMatchesLabel : 'No records'}
                </Typography>
                {props.createNewButton && props.createNewButton}
            </Box>
        ),
        Footer: AutoRefreshGridFooter,
    };
    const showSelectAllBanner = enableSelectAll && total > perPage;
    const origToolbar = components.Toolbar || props.toolbar || null;
    if (showSelectAllBanner) {
        // Inject SelectAllBanner into the grid via ToolbarWithSelectAll;
        // banner props are passed through componentsProps.toolbar
        components.Toolbar = ToolbarWithSelectAll;
    } else if (!components.Toolbar) {
        components.Toolbar = props.toolbar;
    }
    const onSelectionModelChange = (selection) => {
        const newIds = Array.isArray(selection) ? selection : [];
        let changed = false;
        setSelectionModel(prev => {
            if (prev.length === newIds.length && prev.every((id, i) => id === newIds[i])) {
                return prev;
            }
            changed = true;
            return newIds;
        });
        if (!changed) return;
        if (selectAllInProgressRef.current) {
            // Selection changed because doSelectAll set page IDs — don't clear meta
            selectAllInProgressRef.current = false;
        } else {
            // User manually changed selection — clear any active select-all
            setSelectAllMeta(null);
            onSelectionChangeRef.current && onSelectionChangeRef.current(newIds, newIds);
        }
    };
    const onRowClick = (params, ev) => {
        props.onRowClick &&
            props.onRowClick(params, ev, {
                setLoading: setLoading,
            });
    };

    const onPaginationModelChange = (params) => {
        const currentPage = params.page;
        const pageSize = params.pageSize;
        var parameters = history.location.search.split('&');
        var tab = undefined;
        parameters.forEach(param => {
            if (param.includes('tab')) {
                tab = param.split('=')[1];
            }
        });

        if (perPage != pageSize) {
            onPageSizeChange(pageSize);
        } else {
            if ((currentPage - 1) != page) {
                onNewPageChange(currentPage + 1, pageSize, tab);
            }
        }
    }

    const onNewPageChange = (newPage, pageSize, tab) => {
        setPage(newPage);

        if (props.setLastDataGridPage) {
            props.setLastDataGridPage(newPage);
        }

        if (props.disablePageUrlData) {
            return;
        }

        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set('page', newPage);
        searchParams.set('pageSize', pageSize);
        if (tab) {
            searchParams.set('tab', tab);
        }
        history.push({ search: searchParams.toString() });
    };


    const onPageChange = (params) => {
        setPage(params + 1);

        if (props.setLastDataGridPage) {
            props.setLastDataGridPage(params + 1);
        }

        if (props.disablePageUrlData) {
            return;
        }

        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set('page', params + 1);
        history.push({ search: searchParams.toString() });
    };


    const onPageSizeChange = (params) => {
        setPerPage(params);
        setPage(1);

        if (props.setLastDataGridPerPage) {
            props.setLastDataGridPerPage(params);
        }
        if (props.setLastDataGridPage) {
            props.setLastDataGridPage(1);
        }
        if (props.disablePageUrlData) {
            return;
        }

        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set('page', 1);
        searchParams.set('pageSize', params);

        history.push({ search: searchParams.toString() });
    };

    const onSortModelChange = (params) => {
        if (params !== sortModel) {
            setSortModel(params);
        }
    };
    const convertFilters = (flist) => {
        return (flist || [])
            .map((f) => {
                try {
                    return toApiFilter(f, props.columns);
                } catch (e) {
                    console.warning('RecordList.convertFilters: filter=', f, ', exception=', e, ', columns=', props.columns);
                    return null;
                }
            })
            .filter((e) => !!e);
    };
    // The exact filter payload the list request sends: the grid's own column
    // filters plus the caller's static ones. Published to the parent so a bulk
    // action derived from this grid (export) resolves the same rows the list
    // is showing -- the same contract as RecordListPro.onEffectiveFiltersChange.
    const effectiveFilters = React.useMemo(
        () => [...convertFilters(filterList), ...(props.apiFilters || [])],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filterList, props.apiFilters],
    );
    const { onEffectiveFiltersChange } = props;
    React.useEffect(() => {
        if (onEffectiveFiltersChange) {
            onEffectiveFiltersChange(effectiveFilters);
        }
    }, [effectiveFilters, onEffectiveFiltersChange]);

    const onFilterChange = React.useCallback((filterModel) => {
        console.log('onFilterChange', filterModel);
        if (filterModel && filterModel.items) {
            // const fl = filterModel.items.filter((x) => (typeof x.value !== "undefined"));
            const fl = filterModel.items;
            console.log('onFilterChange', fl);
            setFilterList(fl);
        } else {
            setFilterList([]);
        }
    }, []);

    const _sortModel2sort = (sm) => {
        if (sm && sm.length > 0) {
            return { field: sm[0].field, order: sm[0].sort };
        } else if (defaultSortModel && defaultSortModel.length > 0) {
            return { field: defaultSortModel[0].field, order: defaultSortModel[0].sort };
        }
    };
    const handleReorder = (p, idx, nextIdx, direction, ev) => {
        ev.stopPropagation();
        const order = sortModel[0]?.sort; // either 'asc' or 'desc'

        let currentRows = [...rows];
        const sequences = currentRows.map((r) => r.sequence);
        const hasDuplicates = new Set(sequences).size !== sequences.length;
        let rowOrderMap = { direction: direction };

        if (hasDuplicates) {
            const baseSeq = (page - 1) * perPage;
            currentRows = currentRows.map((r, i) => {
                const newSeq = baseSeq + i + 1;
                rowOrderMap[r.id] = newSeq;
                return { ...r, sequence: newSeq };
            });
            const updatedRow = currentRows.find(r => r.id === p.row.id);
            if (updatedRow) {
                p = { ...p, row: updatedRow };
            }
        }

        if (nextIdx === perPage || (idx === 0 && direction === 'up')) {
            rowOrderMap[p.row.id] = p.row.sequence;
            props.onRowOrderUpdate && props.onRowOrderUpdate(rowOrderMap);
            if (hasDuplicates) {
                setRows(currentRows);
                props.setRows?.(currentRows);
                props.onRowsChanged && props.onRowsChanged(currentRows);
            }
            return;
        }

        const nextId = p.api.getRowIdFromRowIndex(nextIdx);
        let nextRow = currentRows.find(r => r.id === nextId);
        if (!nextRow) {
            nextRow = p.api.getRow(nextId);
        }

        const [a, b] = [nextRow.sequence, p.row.sequence];
        rowOrderMap[p.row.id] = a;
        rowOrderMap[nextRow.id] = b;

        const newRows = currentRows
            .map((r) => {
                if (r.id === p.row.id) {
                    return { ...r, sequence: a };
                } else if (r.id === nextRow.id) {
                    return { ...r, sequence: b };
                } else {
                    return r;
                }
            })
            .sort((a, b) => (order === 'desc' ? b.sequence - a.sequence : a.sequence - b.sequence));
        setRows(newRows);
        props.setRows?.(newRows);
        props.onRowsChanged && props.onRowsChanged(newRows);
        props.onRowOrderUpdate && props.onRowOrderUpdate(rowOrderMap);
    };

    const handleDragReorder = (p) => {
        const { oldIndex, targetIndex } = p;
        if (oldIndex == null || targetIndex == null || oldIndex === targetIndex) {
            return;
        }
        const newRows = [...rows];
        const [movedRow] = newRows.splice(oldIndex, 1);
        newRows.splice(targetIndex, 0, movedRow);

        // Reassign the sequence slots already occupied by the rows on this page to the new
        // row order; rows on other pages keep their sequences. If the page contains duplicate
        // sequences, renumber the whole page instead (same as the arrow-based handleReorder).
        const sequences = rows.map((r) => r.sequence);
        const hasDuplicates = new Set(sequences).size !== sequences.length;
        const baseSeq = (page - 1) * perPage;
        const slots = hasDuplicates ? rows.map((r, i) => baseSeq + i + 1) : [...sequences].sort((a, b) => a - b);

        const rowOrderMap = { direction: 'drag' };
        const updatedRows = newRows.map((r, i) => {
            const seq = slots[i];
            if (hasDuplicates || r.sequence !== seq) {
                rowOrderMap[r.id] = seq;
            }
            return r.sequence === seq ? r : { ...r, sequence: seq };
        });
        setRows(updatedRows);
        props.setRows?.(updatedRows);
        props.onRowsChanged && props.onRowsChanged(updatedRows);
        props.onRowOrderUpdate && props.onRowOrderUpdate(rowOrderMap);
    };

    const dragReorderType = {
        type: 'number',
        width: 110,
        renderCell: (p) => {
            const title = dragReorderActive
                ? 'Drag the handle at the start of the row to change the order'
                : dragRowReordering
                  ? `To reorder rows remove filters and sort the table by the '${orderingField}' field`
                  : null;
            // Show the row's rank (1, 2, 3…) rather than the stored sequence value, which is
            // arbitrary. Falls back to the stored value when the view isn't in sequence order
            // (sorted by another column), where a positional rank would be misleading.
            const idx = rows.findIndex((r) => r.id === p.id);
            const label = sequenceOrderedView && idx >= 0 ? (+page - 1) * +perPage + idx + 1 : p.row.sequence;
            return (
                <Tooltip title={title || ''}>
                    <Typography sx={{ width: '100%', textAlign: 'right' }}>
                        {label}
                    </Typography>
                </Tooltip>
            );
        },
    };

    // Pro grids always use the drag-style sequence cell: the arrow buttons below rely on
    // v5-only grid APIs (p.api.getRowIndex) that don't exist on DataGridPro v6.
    const reorderType = isPro ? dragReorderType : {
        type: 'number',
        width: 150,
        renderCell: (p) => {
            const upDisabled = props.staticOptions ? p.api.getRowIndex(p.id) === 0 && page === 1 : !isReorderingAllowed || (p.api.getRowIndex(p.id) === 0 && page === 1);
            const downDisabled = props.staticOptions ? p.api.getRowIndex(p.id) + 1 === p.api.getRowsCount() : !isReorderingAllowed || (page > 1 ? (page - 1) * perPage : 0) + (p.api.getRowIndex(p.id) + 1) === p.api.getRowsCount();
            const title = isReorderingAllowed ? null : `To reorder rows remove filters and sort the table by the '${orderingField}' field`;
            return (
                <div style={{ transform: 'scale(0.85)', gap: '0.5rem', display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Tooltip title={title || 'Move up'}>
                        <span>
                            <Button disabled={upDisabled} onClick={(ev) => handleReorder(p, p.api.getRowIndex(p.id), p.api.getRowIndex(p.id) - 1, 'up', ev)} variant="outlined" size="small">
                                <ArrowUpwardIcon color={upDisabled ? 'inherit' : 'primary'} />
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title={title || 'Move down'}>
                        <span>
                            <Button disabled={downDisabled} onClick={(ev) => handleReorder(p, p.api.getRowIndex(p.id), p.api.getRowIndex(p.id) + 1, 'down', ev)} variant="outlined" size="small">
                                <ArrowDownwardIcon color={downDisabled ? 'inherit' : 'primary'} />
                            </Button>
                        </span>
                    </Tooltip>
                    <Typography sx={{ minWidth: 32, textAlign: 'right' }}>{p.row.sequence}</Typography>
                </div>
            );
        },
    };

    const columns = props.columns
        // .filter(c => ((c.type !== 'reorder') || isReorderingAllowed))
        .map((c) => {
            if (c.type === 'reorder') {
                return { ...c, ...reorderType };
            } else if ((c.type === 'date' || c.type === 'dateTime') && !c.filterOperators) {
                const getDateOperators = props.flavor === 'pro' ? getGridDateOperatorsPro : getGridDateOperators;
                return { ...c, filterOperators: serverDateOperators(getDateOperators, c.type) };
            } else {
                return c;
            }
        })
        .map((c) => {
            const width = persistColumnWidths ? columnWidths[c.field] : undefined;
            if (!width) {
                return c;
            }
            // A flex column ignores width and would snap back to its share of the grid.
            const { flex: _flex, ...rest } = c;
            return { ...rest, width };
        });

    React.useEffect(() => {
        if (props.staticOptions) {
            setTotal(props.staticOptions.length);
            return;
        }
        let active = true;
        let ac = new AbortController();

        (async () => {
            setLoading(true);
            fetchInFlightRef.current = true;
            const params = {
                signal: ac.signal,
                convert: props.convert,
                pagination: { page, perPage },
                sort: _sortModel2sort(sortModel),
                filter: {
                    query: props.query,
                    filters: JSON.stringify([...convertFilters(filterList), ...(props.apiFilters || [])]),
                    ...(props.extraQueryParams || {}),
                },
                urlSuffix: props.urlSuffix,
            };
            console.log('recordList', params);
            try {
                const res = await dataAPI(GET_LIST, resource, params);
                if (!active) {
                    return;
                }
                if (res.statusCode === 500) {
                    enqueueSnackbar('Sorry, our server is under maintenance, for more information please contact support', { variant: 'error' });
                }
                if (res.statusCode === 429) {
                    // The server capped concurrent runs of this list (Agent Metrics
                    // does this for its ClickHouse report); the body is an empty page.
                    enqueueSnackbar('This report is busy right now. Please try again in a few seconds.', { variant: 'warning' });
                }
                if (res.total < 0) {
                    console.log('recordList: data fetched', res);
                    const num_to_show_on_page = (res.data || []).length;
                    const prev_pages_total = (page - 1) * perPage;
                    if ((res.data || []).length >= perPage) {
                        setTotal(prev_pages_total + num_to_show_on_page + 1); // set to something bigger than the current page size to show the paginator controls
                    }
                    else {
                        setTotal(prev_pages_total + num_to_show_on_page);
                    }
                    if (res.async_count_handle) {
                        setAsyncCountHandle(res.async_count_handle);
                    }
                } else {
                    setTotal(res.total);
                }
                const rowData = dedupeRowsById(res.data);
                setRows(rowData);
                props.setRows?.(rowData);
                props.onRowsChanged && props.onRowsChanged(rowData);
                // Clear loading only after the rows are set — these updates are
                // not batched here (React 17, promise context), so the reverse
                // order flashes the empty-state message before the rows appear.
                setLoading(false);
            } catch (e) {
                if (e.name !== 'AbortError') {
                    throw e;
                }
            } finally {
                // Only the current effect instance owns the flag: a superseded fetch
                // settling late must not clear it under the fetch that replaced it.
                if (active) {
                    fetchInFlightRef.current = false;
                }
            }
        })();

        return () => {
            active = false;
            ac.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataAPI, resource, page, perPage, sortModel, filterList, props.query, props.deps, props.hiddenAPIFilters, props.apiFilters, refreshTriggerValue]);

    React.useEffect(() => {
        if (!asyncCountHandle) { return; }
        let active = true;
        const refreshAsyncCount = () => {
            dataAPI(GET_ONE, resource, { urlSuffix: 'async_count/', filter: { handle: asyncCountHandle } }).then((res) => {
                if (!active) { return; }
                if (res.statusCode === 200) {
                    if (res.data >= 0) {
                        console.log('recordList: got async count', res.data);
                        setTotal(res.data);
                        setAsyncCountHandle(null);
                        clearInterval(tm);
                    }
                } else {
                    console.log('got error', res);
                }
            });
        };
        const tm = setInterval(refreshAsyncCount, 1000);
        refreshAsyncCount();
        return () => {
            active = false;
            clearInterval(tm);
        };
    }, [dataAPI, resource, sortModel, filterList, props.query, props.deps, props.hiddenAPIFilters, props.apiFilters, asyncCountHandle, refreshTriggerValue]);

    const onEditCellChangeCommitted = (change, ev) => {
        props.onEditCellChangeCommitted && props.onEditCellChangeCommitted(change, ev);
    };


    const GridComponent = props.flavor == "pro" ? DataGridPro : DataGrid;

    // console.log(`RecordList[${resource}]: total=${total} page=${page} loaded=${rows?.length} | sort=`, sortModel, 'loading=', loading || props.relatedLoading);
    if (rows === null) {
        return <GridSkeleton />;
    } else {
        const filteredRows = props.staticOptions
            ? props.staticOptions
            : rows.filter((row) => {
                return (props.deletedList || []).indexOf(row.id) < 0;
            });

        if (isSmallScreen && props.listItemRenderer) {
            const hasPreviousPage = page > 1;
            const hasNextPage = page * perPage < total;
            const gridApi = {
                onRowClick: onRowClick,
                columns: props.columns,
                rows: props.staticOptions ? props.staticOptions : props.rows,
                filteredRows: filteredRows,
                props: props,
            };
            const goToPreviousPage = () => hasPreviousPage && setPage(page - 1);
            const goToNextPage = () => hasNextPage && setPage(page + 1);

            const listRootClass = props.mobileNaturalHeight
                ? classes.itemListRootNatural
                : hasPreviousPage || hasNextPage
                  ? classes.itemListRootNoMarginBottom
                  : classes.itemListRoot;
            const wrapperStyle = props.mobileNaturalHeight
                ? undefined
                : {
                      height: props.limitHeightForMobile ? props.limitHeightForMobile : '100%',
                      minHeight: 0,
                  };

            return (
                <div className={clsx(classes.mobileWrapper, props.mobileNaturalHeight && classes.mobileWrapperNatural)} style={wrapperStyle}>
                    {loading ? (
                        // In-flow spinner (same as the line set edit screen) —
                        // position:fixed breaks under transformed ancestors and
                        // can end up clipped or off-screen in the mobile layouts.
                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                            <CircularProgress color="primary" />
                        </Box>
                    ) : filteredRows.length > 0 ? (
                        <>
                            <Box className={clsx(listRootClass, !props.disableListBorder && classes.withBorder)}>
                                <List>{filteredRows.map((row, idx) => props.listItemRenderer(row, idx, filteredRows, gridApi))}</List>
                            </Box>
                            {hasPreviousPage || hasNextPage ? (
                                <div className={clsx(classes.paginationButtons)}>
                                    <Tooltip title="Previous page">
                                        <span>
                                            <Button disabled={!hasPreviousPage} variant="outlined" color="primary" onClick={goToPreviousPage}>
                                                <ArrowBackIcon size="small" />
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Typography variant="body1">
                                        Page {page} of {totPages}
                                    </Typography>
                                    <Tooltip title="Next page">
                                        <span>
                                            <Button disabled={!hasNextPage} variant="outlined" color="primary" onClick={goToNextPage}>
                                                <ArrowForwardIcon size="small" />
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <Box display="flex" p={2} justifyContent={'center'} mt={20}>
                            <Typography>{props.noItemsFoundMessage ? props.noItemsFoundMessage : 'No records'}</Typography>
                        </Box>
                    )}
                </div>
            );
        } else {
            return (
                <GridComponent
                    apiRef={props.apiRef}
                    className={props.className}
                    style={{ border: props.noBorder && 'none' }}
                    pagination
                    checkboxSelection={props.checkboxSelection !== void 0 ? props.checkboxSelection : true}
                    autoPageSize={props.autoPageSize}
                    autoHeight={props.autoHeight}
                    filterMode="server"
                    paginationMode={props.paginationMode || "server"}
                    sortingMode={props.sortingMode || 'server'}
                    sortModel={dragReorderActive ? [] : sortModel}
                    onSortModelChange={!props.ignoreHeaderOrdering ? onSortModelChange : null}
                    onFilterModelChange={onFilterChange}
                    {...(isPro
                        ? {
                              // DataGridPro is v6 — pagination and selection props were renamed
                              paginationModel: { page: +page - 1, pageSize: +perPage },
                              pageSizeOptions: rowsPerPageOptions,
                              rowSelectionModel: selectionModel,
                              onRowSelectionModelChange: onSelectionModelChange,
                              disableRowSelectionOnClick: disableSelectionOnClick,
                              columnHeaderHeight: props.headerHeight != null ? props.headerHeight : 56,
                              rowReordering: dragReorderActive,
                              onRowOrderChange: handleDragReorder,
                          }
                        : {
                              page: page - 1,
                              pageSize: perPage,
                              rowsPerPageOptions: rowsPerPageOptions,
                              onPageChange: onPageChange,
                              onPageSizeChange: onPageSizeChange,
                              selectionModel: selectionModel,
                              onSelectionModelChange: onSelectionModelChange,
                              disableSelectionOnClick: disableSelectionOnClick,
                              headerHeight: props.headerHeight != null ? props.headerHeight : 56,
                          })}
                    rowCount={total || 0}
                    columns={columns}
                    // Renders every row of the page into the DOM so browser Ctrl+F can find
                    // and scroll to off-screen rows. Only set this on pages whose page size
                    // is bounded (max 500) — it trades render speed for findability.
                    disableVirtualization={props.disableVirtualization}
                    keepNonExistentRowsSelected={props.keepNonExistentRowsSelected}
                    onRowClick={onRowClick}
                    rowHeight={props.rowHeight || 52}
                    getRowHeight={props.getRowHeight || null}
                    density="compact"
                    rows={filteredRows}
                    loading={loading || props.relatedLoading}
                    components={components}
                    componentsProps={{
                        ...props.slotProps,
                        toolbar: {
                            ...(props.slotProps?.toolbar || {}),
                            ...(showSelectAllBanner ? {
                                OrigToolbar: origToolbar,
                                selectAllBannerProps: {
                                    selectedIds: selectionModel,
                                    selectAllMeta,
                                    onSelectAll: doSelectAll,
                                    onDeselectAll: doDeselectAll,
                                    loading: selectAllLoading,
                                },
                            } : {}),
                        },
                        footer: {
                            deps: refreshTrigger,
                            intervals: props.intervals,
                            autorefresh: props.autorefresh,
                            onTick: props.onAutoRefreshTick,
                            gridFlavor: props.flavor === 'pro' ? 'pro' : 'community',
                        }
                    }}
                    pinnedRows={{ bottom: props.totalRow }}
                    onEditCellChangeCommitted={onEditCellChangeCommitted}
                    disableColumnMenu={props.disableColumnMenu}
                    disableColumnFilter={props.disableColumnFilter}
                    disableColumnSelector={props.disableColumnSelector}
                    {...(isPro
                        ? {
                              disableColumnReorder: props.disableColumnReorder,
                              onColumnWidthChange: onColumnWidthChange,
                          }
                        : {})}
                    columnVisibilityModel={hasOwnColumnVisibilityModel ? props.columnVisibilityModel : internalColumnVisibilityModel}
                    onColumnVisibilityModelChange={onColumnVisibilityModelChange}
                    initialState={{
                        ...props.initialState,
                        pagination: { paginationModel: { pageSize: perPage } },
                    }}
                    onPaginationModelChange={onPaginationModelChange}
                    onUpdateCallback={props.onUpdateCallback}
                    getRowClassName={(params) =>
                        params.id === 'totals' ? 'MuiDataGrid-row--totals' : ''
                    }
                    sx={{
                        '&.MuiDataGrid-root': {
                            border: props.borderDisplay,
                        },
                        '& .MuiDataGrid-main': {
                            minHeight: props.minHeight,
                            minWidth: props.minWidth,
                            maxHeight: props.maxHeight,
                            maxWidth: props.maxWidth,
                        },
                        '& .MuiDataGrid-cell': {
                            border: props.borderDisplay,
                        },
                        '& .MuiDataGrid-row:last-child': {
                        },
                        // The Pro reorder cell renders the row id next to the drag handle by default
                        '& .MuiDataGrid-rowReorderCellPlaceholder': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-virtualScrollerContent': {
                            paddingBottom: props.isExporting ? `${400 + perPage * 4}px` : null,
                            marginTop: props.isExporting ? 5 : 0,
                        },
                        '& .MuiDataGrid-footerContainer': {
                            //display: props.isExporting ? "none" : null,
                            // move the footer to the top when exporting to make it clear how many are being exported
                            position: props.isExporting ? 'absolute' : null,
                            top: props.isExporting ? 0 : null,
                            fontStyle: props.isExporting ? "italic" : null
                        },
                        '& .MuiTablePagination-displayedRows:before': {
                            content: props.isExporting ? '"Message history records "' : null,
                        },
                        '& .MuiDataGrid-toolbarContainer': {
                            display: props.isExporting ? "none" : null
                        },
                        '& .MuiTablePagination-actions': {
                            display: props.isExporting ? "none" : null
                        },
                        '& .MuiTablePagination-input': {
                            display: props.isExporting ? "none" : null
                        },
                        '& .MuiTablePagination-selectLabel': {
                            display: props.isExporting ? "none" : null
                        },

                        "& .MuiDataGrid-columnHeaderTitle": props.colTitlesMultiLine && {
                            overflow: "visible",
                            lineHeight: "1.33rem",
                            whiteSpace: "normal",
                            textAlign: "center",
                            vertcialAlign: "bottom"
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            display: props.headerDisplay,
                            height: props.colTitlesMultiLine && `${props.colTitlesMultiLineHeight || 64}px !important`,
                            maxHeight: props.colTitlesMultiLine && `${props.colTitlesMultiLineHeight || 64}px !important`,
                            minHeight: props.colTitlesMultiLine && `${props.colTitlesMultiLineHeight || 64}px !important`,
                        },
                        "& .MuiDataGrid-columnHeader": props.colTitlesMultiLine && {
                            height: `${props.colTitlesMultiLineHeight || 64}px !important`,
                        },
                        "& .MuiDataGrid-columnHeaderTitleContainer": props.colTitlesMultiLine && {
                            height: `${props.colTitlesMultiLineHeight || 64}px !important`,
                            display: "table"
                        },
                        "& .MuiDataGrid-columnHeaderTitleContainerContent": props.colTitlesMultiLine && {
                            display: "table-cell",
                            verticalAlign: "bottom",
                            paddingBottom: "4px",
                        },
                        "& .MuiDataGrid-virtualScroller": props.colTitlesMultiLine && {
                            //marginTop: "64px !important",
                        },
                        "& .MuiDataGrid-row--totals": {
                            fontWeight: 'bold',
                            backgroundColor: '#f9f9f9', // optional
                        },
                    }}
                    getRowId={(row) => {
                        if (row?.id !== undefined && row?.id !== null && row.id !== '') {
                            return row.id;
                        }
                        return `row-fallback-${JSON.stringify(row).replace(/\s+/g, '').slice(0, 96)}`;
                    }}
                />
            );
        }
    }
};

export default RecordList;
